import React, { FunctionComponent, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import {
  Box,
  Button,
  FormHelperText,
  IconButton,
  Popover,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";

import {
  UnitDescription,
  UtilityBillByIdDTO,
  SiteByIdDTO,
  AllowedValuesDTO,
  DocumentByIdDTO,
} from "api";

import {
  EsgArrowDown,
  EsgArrowUp,
  EsgNotesIcon,
  EsgPencilIcon,
  InfoIcon,
  Infounit,
  QuestionIcon,
} from "assets";

import {
  AutocompleteField,
  COLORS,
  DatePickerFormField,
  Esg12Label,
  H5Bold,
  IMaskFormTextField,
  Label12SemiBold,
  Label14SemiBold,
  TextField,
  USNumberMask2,
  UTILITY_BILL_TYPES,
  Loading,
} from "components";

import {
  useAllCollectors,
  useCollectorsBySiteId,
  useGetAllowedValues,
  useNotify,
  useGetConfigParameter,
  useAmplitude,
} from "hooks";

import { REQUIRED_VALIDATION_MESSAGE } from "consts";
import { MaybeNull } from "types";
import { useUserRole } from "recoils";
import {
  capitalizeAllStringWithSeparator,
  getLoggedUserData,
  isFeatureEnabled,
  getTextFieldColorStateProps,
} from "utils";
import { IMaskFormTextFieldNumber } from "components/inputs/imask-text-field-number";

import { COLLECTOR_LABEL } from "pages/authenticated/documents/consts";
import {
  compareCollector,
  getCollectorString,
} from "pages/authenticated/documents/utils";

import {
  DateFormatEditor,
  ValueFormatEditor,
  UtilityBillSaveChangesDialog,
  UtilityBillEditNotesDialog,
  AddOtherSitesDialog,
  SaveConfirmButtons,
  StatusChangeButtons,
  RenderLabelWithButton,
} from "./components";
import {
  getAnomaliesData,
  getTextFieldNoErrorProps,
  getCollectorsOptions,
  areDatesInvalid,
  findUtilityTypeInfo,
  hasInitialFormValues,
  calculateUnitCost,
  shouldShowSubtype,
  isCollectorEmpty as isCollectorEmptyUtil,
  findCollectorByString,
  findCollectorById,
  getCollectorNotes,
} from "./utils";

import {
  useUtilityBillForm,
  useUtilityBillMutations,
  useUtilityBillButtonStates,
} from "./hooks";
import {
  useUIHandlers,
  useCollectorHandlers,
  createFormHandlers,
  useSiteHandlers,
  useNotesHandlers,
  useStatusHandlers,
} from "./handlers";
import { DATES_VERIFICATION_ANOMALY } from "./consts";

import { Anomaly } from "../components";
import { UtilityBillStatusKey } from "../types";

export const UtilityBillFormView: FunctionComponent<{
  // Edit bill mode
  utilityBill?: UtilityBillByIdDTO;
  isLoading?: boolean;
  // Create new bill mode
  document?: DocumentByIdDTO;
  onClose?: () => void;
}> = ({ utilityBill, isLoading, document, onClose }) => {
  const isCreateMode = !!document && !utilityBill;

  // ---- Hooks ----
  const notify = useNotify();
  const userRole = useUserRole();
  const [isSaveChangesDialogOpen, setIsSaveChangesDialogOpen] = useState(false);
  const { superadmin: isUserSuperadmin } = getLoggedUserData();
  const initialSiteId = isCreateMode
    ? document?.siteId ?? 0
    : utilityBill?.siteId ?? 0;
  const initialSiteName = isCreateMode
    ? document?.siteName ?? ""
    : utilityBill?.siteName ?? "";
  const [currentSite, setCurrentSite] = useState<{
    id: number | undefined;
    name: MaybeNull<string>;
  }>({
    id: initialSiteId,
    name: initialSiteName,
  });
  const isSiteChanged = !isCreateMode && currentSite.id !== utilityBill?.siteId;
  const shouldUseAllCollectors = currentSite.id === 0 || currentSite.id === -1;
  const { data: collectorsFromSiteDataResult } = useCollectorsBySiteId(
    shouldUseAllCollectors ? null : currentSite.id ?? null,
  );
  const { data: allCollectorsData } = useAllCollectors(shouldUseAllCollectors);
  const collectorsFromSiteData = shouldUseAllCollectors
    ? allCollectorsData
    : collectorsFromSiteDataResult;
  const { data: configParameter } = useGetConfigParameter();
  const { trackEvent } = useAmplitude();

  // ---- API's ----
  const { data: allowedValues } = useGetAllowedValues();
  const mutations = useUtilityBillMutations();
  const {
    queryClient,
    isRecalculating,
    manuallyApprove,
    isManuallyApproving,
    update,
    isUpdating,
    createBill,
    isCreating,
    updateBillStatus,
    updateCollectorRequest,
    formatData,
    getFormats,
  } = mutations;

  const [newCollectorId, setNewCollectorId] = useState<number | undefined>(
    undefined,
  );

  const isAdditionalStatusesOn = isFeatureEnabled(
    configParameter,
    "unusable_status_enabled",
  );
  const utilityBillStatus: UtilityBillStatusKey = !isCreateMode
    ? ((utilityBill as UtilityBillByIdDTO)
        ?.statusFilter as UtilityBillStatusKey)
    : (undefined as any);

  // ---- Use Form Hook ----
  const {
    register,
    control,
    watch,
    reset,
    handleSubmit,
    setValue,
    formState,
    defaultValues,
    isWarningFields,
  } = useUtilityBillForm({
    isCreateMode,
    document,
    utilityBill,
    collectorsFromSiteData,
    newCollectorId,
    currentSite,
    isSiteChanged,
  });

  const watchValues: any = watch();

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  // ---- State ----
  const [isManualApproveDisabled, setIsManualApproveDisabled] =
    useState<boolean>(false);
  const [initialBtnValue, setInitialBtnValue] = useState<any>();
  const [buttonName, setButtonName] = useState("");
  const [openAddSiteDialog, setOpenAddSiteDialog] = useState(false);
  const [openDateFormatEditor, setOpenDateFormatEditor] = useState(false);
  const [openValueFormatEditor, setOpenValueFormatEditor] = useState(false);
  const [valueFormatName, setValueFormatName] = useState("");
  const [showsubType, setShowsubType] = useState(false);
  const [unitCostValue, setUnitCostValue] = useState("");
  const [co2Emission, setCo2Emission] = useState<MaybeNull<number>>(
    utilityBill?.co2Emission ?? null,
  );
  const [availableUnits, setAvailableUnits] = useState<UnitDescription[]>([]);
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [openNotesEdit, setOpenNotesEdit] = useState<boolean>(false);
  const [notes, setNotes] = useState<string>("");
  const [notesError, setNotesError] = useState<boolean>(false);
  const [isCollectorEmpty, setIsCollectorEmpty] = useState<boolean>(
    isCollectorEmptyUtil(watchValues.collector),
  );
  const collectorsOptions = getCollectorsOptions(collectorsFromSiteData);

  const invalidDates = areDatesInvalid(startDate, endDate);

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;
  const isDisable =
    !isCreateMode &&
    (utilityBill?.status === "LOCKED" || utilityBill?.status === "SIGNED");

  const siteData = {
    ...watchValues,
    consumptionUnit: watchValues.measuredUnit,
    utilityTypeName: capitalizeAllStringWithSeparator(
      watchValues.utilityTypeName,
      " ",
    ),
  };

  const closeSaveChangesDialog = () => setIsSaveChangesDialogOpen(false);
  const openSaveChangesDialog = () => setIsSaveChangesDialogOpen(true);

  // ---- Button Disabled States ----
  const isProcessing = isRecalculating || isUpdating || isManuallyApproving;

  const {
    isSaveButtonDisabled,
    isConfirmButtonDisabled,
    isNotApplicableInvalidDataButtonDisabled,
    isNotApplicableCostOnlyButtonDisabled,
    isCreateButtonDisabled,
  } = useUtilityBillButtonStates({
    formState,
    utilityBillStatus,
    isProcessing,
    invalidDates,
    isCollectorEmpty,
    isManualApproveDisabled,
    initialBtnValue,
    watchValues,
    newCollectorId,
    isCreating,
    isAuditor: userRole.isAuditor,
  });

  // ---- Handlers ----
  const formHandlers = createFormHandlers({
    isCreateMode,
    document,
    utilityBill,
    collectorsFromSiteData,
    queryClient,
    notify,
    reset,
    watch,
    setNewCollectorId,
    setIsManualApproveDisabled,
    closeSaveChangesDialog,
    buttonName,
    setButtonName,
    trackEvent,
    onClose,
    manuallyApprove,
    createBill,
    update,
  });

  const handleSaveClick = handleSubmit(formHandlers.handleSaveClick);
  const handleManualApproveClick = handleSubmit(
    formHandlers.handleManualApproveClick,
  );

  const { handleClickPopover, handleClosePopover, handleConfirmData } =
    useUIHandlers({
      utilityBill,
      setButtonName,
      setAnchorEl,
      handleManualApproveClick,
      openSaveChangesDialog,
      notify,
    });

  // Collector handlers
  const { handleCollectorChange, handleSetNewCollectorId } =
    useCollectorHandlers({
      collectorsFromSiteData,
      setValue,
      setIsCollectorEmpty,
      setNewCollectorId,
      currentSite,
      queryClient,
    });

  const { handleOtherSitesButtonClick, handleApplyNewSite, handleSetNewSite } =
    useSiteHandlers({
      isCreateMode,
      currentSite,
      setCurrentSite,
      setValue,
      setNewCollectorId,
      setIsCollectorEmpty,
      setOpenAddSiteDialog,
    });

  const {
    handleEditNotes,
    handleSaveCollectorNotes,
    handleCloseEditNotes,
    handleOpenEditNotes,
  } = useNotesHandlers({
    notes,
    setNotes,
    setNotesError,
    setOpenNotesEdit,
    allowedValues,
    utilityBill,
    document,
    newCollectorId,
    collectorsFromSiteData,
    currentSite,
    defaultValues,
    notify,
    updateCollectorRequest,
    handleClosePopover,
  });

  const { handleSetStatus } = useStatusHandlers({
    utilityBill,
    watchValues,
    queryClient,
    notify,
    updateBillStatus,
    setButtonName,
  });

  useEffect(() => {
    if (watchValues.utilityTypeName && allowedValues) {
      const selectedUtilityTypeInfo = findUtilityTypeInfo(
        allowedValues,
        watchValues.utilityTypeName,
      );
      if (selectedUtilityTypeInfo) {
        setAvailableUnits(selectedUtilityTypeInfo.unitDescription || []);
      } else {
        setAvailableUnits([]);
      }
    } else {
      setAvailableUnits([]);
    }
  }, [watchValues.utilityTypeName, allowedValues]);

  useEffect(() => {
    if (!isCreateMode) {
      setCo2Emission(utilityBill?.co2Emission ?? 0);
    }
  }, [
    isCreateMode,
    utilityBill?.co2Emission,
    utilityBill?.usageDifferenceToPreviousBill,
  ]);

  // Autofill fields when a new collector is selected in create mode
  useEffect(() => {
    if (isCreateMode && watchValues.collector && collectorsFromSiteData) {
      const selectedCollector = findCollectorByString(
        collectorsFromSiteData,
        watchValues.collector,
        compareCollector,
      );

      if (selectedCollector) {
        setValue("siteName", selectedCollector.siteName || "");
        setValue("provider", selectedCollector.providerName || "");
        setValue("currency", selectedCollector.currency || "");
        setValue("measuredUnit", selectedCollector.consumptionUnit || "");
      }
    }
  }, [isCreateMode, watchValues.collector, collectorsFromSiteData, setValue]);

  useEffect(() => {
    setUnitCostValue(
      calculateUnitCost(watchValues.currency, watchValues.measuredUnit),
    );
  }, [watchValues.currency, watchValues.measuredUnit]);

  useEffect(() => {
    setInitialBtnValue(hasInitialFormValues(defaultValues));
  }, [defaultValues]);

  useEffect(() => {
    setShowsubType(shouldShowSubtype(watchValues.utilityTypeName));
  }, [watchValues.utilityTypeName]);

  useEffect(() => {
    // Only validate if both dates are selected
    if (!startDate || !endDate) {
      return;
    }

    if (areDatesInvalid(startDate, endDate)) {
      notify.error(
        "Invalid Date Entry: The start date cannot be later than the end date. Please review and correct the dates you've entered.",
      );
    }
  }, [startDate, endDate, notify]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const collectorId = newCollectorId ?? utilityBill?.collectorId;
    if (!isCreateMode && collectorId) {
      getFormats(collectorId);
    }

    // Reset form when defaultValues change
    // Only reset if form is not dirty to avoid overwriting user changes
    if (!formState.isDirty) {
      reset(defaultValues);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isCreateMode, defaultValues, getFormats]);

  // Handle new collector selection: populate all fields when a new collector is added/selected
  useEffect(() => {
    if (newCollectorId === undefined || !collectorsFromSiteData) {
      return;
    }

    const selectedCollector = findCollectorById(
      collectorsFromSiteData,
      newCollectorId,
    );

    if (selectedCollector) {
      const collectorString = getCollectorString(selectedCollector);
      handleCollectorChange(collectorString);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newCollectorId, collectorsFromSiteData]);

  // Initialize notes from collector when collector changes
  useEffect(() => {
    if (!isCreateMode) {
      const collectorId = newCollectorId ?? utilityBill?.collectorId;
      setNotes(getCollectorNotes(collectorsFromSiteData, collectorId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    newCollectorId,
    utilityBill?.collectorId,
    collectorsFromSiteData,
    isCreateMode,
  ]);

  return (
    <>
      <Box
        component="form"
        sx={{
          padding: "24px 8px",
          overflowY: "auto",
          maxHeight: "calc(100dvh - 200px)",
        }}
      >
        <Box component="section">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingRight: 2,
            }}
          >
            <H5Bold mb={2}>Client and provider</H5Bold>
            {!isCreateMode && (
              <div>
                <Button
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "319px",
                    height: "29px",
                    padding: "0 24px 0 12px",
                    borderRadius: 2,
                  }}
                  aria-describedby={id}
                  variant="outlined"
                  onClick={handleClickPopover}
                  disabled={notes === null || notes === ""}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-start",
                      alignItems: "flex-end",
                      gap: 1,
                    }}
                  >
                    <EsgNotesIcon />
                    Notes for Collector
                  </Box>
                  {open ? (
                    <EsgArrowDown />
                  ) : (
                    <EsgArrowUp
                      color={
                        notes === null || notes === "" ? "grey" : COLORS.Romance
                      }
                    />
                  )}
                </Button>
                <Popover
                  sx={{
                    marginTop: 1,
                    "& > div:nth-of-type(3)": {
                      borderRadius: 3,
                      border: "1px solid #D5D5D5",
                    },
                  }}
                  id={id}
                  open={open}
                  anchorEl={anchorEl}
                  onClose={handleClosePopover}
                  anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "left",
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      width: "319px",
                      display: "flex",
                      alignItems: "flex-start",
                      gap: 2,
                    }}
                  >
                    <Box
                      sx={{ cursor: "pointer" }}
                      onClick={handleOpenEditNotes}
                    >
                      <EsgPencilIcon />
                    </Box>
                    <Typography
                      sx={{
                        fontSize: "14px",
                        fontStyle: "italic",
                        fontFamily: "Inter",
                      }}
                    >
                      {notes}
                    </Typography>
                  </Box>
                </Popover>
              </div>
            )}
          </Box>
          <Box mb={2}>
            <TextField
              label="Site"
              fullWidth
              {...register("siteName")}
              disabled
            />
          </Box>
          <TextField
            label="Provider"
            fullWidth
            disabled
            {...register("provider")}
          />
        </Box>
        <Box component="section" mb={3} mt={3}>
          <H5Bold mb={2}>Basic data</H5Bold>
          {/* Dates */}
          <Stack direction="row" mb={2} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <DatePickerFormField
                name="startDate"
                label="Start date"
                control={control}
                disabled={userRole.isAuditor || (!isCreateMode && isDisable)}
                {...(isCreateMode
                  ? getTextFieldColorStateProps("startDate", formState.errors)
                  : getTextFieldNoErrorProps("startDate", isWarningFields))}
                rules={isCreateMode ? { required: true } : undefined}
              />
              {!isCreateMode && isUserSuperadmin === true && (
                <Esg12Label
                  onClick={() => setOpenDateFormatEditor(true)}
                  sx={{
                    textDecoration: "underline",
                    mt: "6px",
                    cursor: "pointer",
                  }}
                >
                  Change Value format?
                </Esg12Label>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <DatePickerFormField
                name="endDate"
                label="End date"
                control={control}
                disabled={userRole.isAuditor || (!isCreateMode && isDisable)}
                {...(isCreateMode
                  ? getTextFieldColorStateProps("endDate", formState.errors)
                  : getTextFieldNoErrorProps("endDate", isWarningFields))}
                rules={isCreateMode ? { required: true } : undefined}
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Controller
              name="collector"
              control={control}
              rules={isCreateMode ? { required: true } : undefined}
              render={({ field }) => (
                <AutocompleteField
                  key={isCreateMode ? "create" : utilityBill?.id}
                  textFieldProps={{
                    label: RenderLabelWithButton({
                      label: COLLECTOR_LABEL,
                      onClickOtherSites: handleOtherSitesButtonClick,
                      siteId: currentSite.id,
                      isEmpty: isCollectorEmpty,
                      allowedValues: allowedValues || ({} as AllowedValuesDTO),
                      siteData: siteData || ({} as SiteByIdDTO),
                      setNewSite: handleSetNewSite,
                      setNewCollectorId: handleSetNewCollectorId,
                    }),
                    ...(isCreateMode
                      ? getTextFieldColorStateProps(
                          "collector",
                          formState.errors,
                        )
                      : getTextFieldNoErrorProps("collector", isWarningFields)),
                    disabled:
                      userRole.isAuditor || (!isCreateMode && isDisable),
                  }}
                  autocompleteProps={{
                    ...field,
                    onChange: (e, data) => {
                      field.onChange(data);
                      handleCollectorChange(data as string | null);
                    },
                    options: Array.from(
                      new Set(collectorsOptions.map((item: any) => item)),
                    ),
                    disabled:
                      userRole.isAuditor || (!isCreateMode && isDisable),
                  }}
                />
              )}
            />
            {((isCreateMode &&
              watchValues.collector?.startsWith(
                UTILITY_BILL_TYPES["VEHICLE FUEL"],
              )) ||
              (!isCreateMode &&
                UTILITY_BILL_TYPES["VEHICLE FUEL"] === utilityBill?.type)) && (
              <IMaskFormTextField
                textFieldProps={{
                  label: "Traveled miles",
                  fullWidth: true,
                  // @ts-ignore
                  mask: USNumberMask2,
                  disabled: userRole.isAuditor,
                  ...(isCreateMode
                    ? getTextFieldColorStateProps(
                        "traveledMiles",
                        formState.errors,
                      )
                    : getTextFieldNoErrorProps(
                        "traveledMiles",
                        isWarningFields,
                      )),
                }}
                control={control}
                name="traveledMiles"
                rules={isCreateMode ? { required: true } : undefined}
              />
            )}
          </Stack>
          {!isCreateMode && (
            <Stack direction="row" mb={2} mt={3} spacing={2.5}>
              <IMaskFormTextField
                textFieldProps={{
                  label: "Utility type",
                  fullWidth: true,
                  disabled: true,
                }}
                control={control}
                name="utilityTypeName"
              />
              {showsubType && (
                <IMaskFormTextField
                  textFieldProps={{
                    label: "Subtype",
                    fullWidth: true,
                    disabled: true,
                  }}
                  control={control}
                  name="subtype"
                />
              )}
              <IMaskFormTextField
                textFieldProps={{
                  label: "Account Number",
                  fullWidth: true,
                  disabled: true,
                }}
                control={control}
                name="accountNumber"
              />
              <IMaskFormTextField
                textFieldProps={{
                  label: "Meter Number",
                  fullWidth: true,
                  disabled: true,
                }}
                control={control}
                name="meterNumber"
              />
            </Stack>
          )}
          {isCreateMode &&
            watchValues.collector?.startsWith(
              UTILITY_BILL_TYPES.REFRIGERANTS,
            ) &&
            allowedValues && (
              <Controller
                name="subtype"
                control={control}
                rules={
                  isCreateMode
                    ? { required: REQUIRED_VALIDATION_MESSAGE }
                    : undefined
                }
                render={({ field }) => (
                  <AutocompleteField
                    textFieldProps={{
                      label: "Subtype",
                      ...getTextFieldColorStateProps(
                        "subtype",
                        formState.errors,
                      ),
                    }}
                    autocompleteProps={{
                      ...field,
                      onChange: (e, data) => {
                        field.onChange(data);
                      },
                      options:
                        Object.keys(allowedValues.utilitySubtypesMap).filter(
                          (ut: string) => ut === "Refrigerants",
                        ) || [],
                    }}
                  />
                )}
              />
            )}
        </Box>
        <Box component="section" mb={3}>
          <H5Bold mb={2}>Invoice items</H5Bold>
          <Stack direction="row" mb={2} spacing={3}>
            <Box sx={{ flex: 1 }}>
              <IMaskFormTextFieldNumber
                {...register("measuredValue")}
                control={control}
                name="measuredValue"
                label="Usage"
                disabled={isCreateMode ? userRole.isAuditor : isDisable}
                rules={isCreateMode ? { required: true } : undefined}
                {...(isCreateMode
                  ? getTextFieldColorStateProps(
                      "measuredValue",
                      formState.errors,
                    )
                  : {})}
              />
              {!isCreateMode && isUserSuperadmin === true && (
                <Esg12Label
                  onClick={() => {
                    setOpenValueFormatEditor(true);
                    setValueFormatName("usageFormat");
                  }}
                  sx={{
                    textDecoration: "underline",
                    mt: "6px",
                    cursor: "pointer",
                  }}
                >
                  Change Value format?
                </Esg12Label>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="measuredUnit"
                control={control}
                rules={
                  isCreateMode
                    ? { required: REQUIRED_VALIDATION_MESSAGE }
                    : undefined
                }
                render={({ field }) => (
                  <AutocompleteField
                    disabled={isCreateMode ? userRole.isAuditor : isDisable}
                    textFieldProps={{
                      label: (
                        <span>
                          Unit{" "}
                          {!isCreateMode &&
                            utilityBill?.unitCalculationFormula && (
                              <Tooltip
                                title={
                                  <pre>
                                    {utilityBill?.unitCalculationFormula}
                                  </pre>
                                }
                                placement="top"
                                arrow
                              >
                                <IconButton
                                  aria-label="info"
                                  size="small"
                                  sx={{
                                    maxHeight: "23px",
                                    maxWidth: "23px",
                                    mt: -0.4,
                                  }}
                                >
                                  <Infounit />
                                </IconButton>
                              </Tooltip>
                            )}
                        </span>
                      ),
                      ...(isCreateMode
                        ? getTextFieldColorStateProps(
                            "measuredUnit",
                            formState.errors,
                          )
                        : getTextFieldNoErrorProps(
                            "measuredUnit",
                            isWarningFields,
                          )),
                      fullWidth: true,
                    }}
                    autocompleteProps={{
                      ...field,
                      disabled: isCreateMode ? userRole.isAuditor : isDisable,
                      onChange: (e, data) => {
                        field.onChange(data);
                      },
                      options: availableUnits
                        ? availableUnits.map(
                            (item) => `${item.unit} (${item.description})`,
                          )
                        : [],
                    }}
                  />
                )}
              />
            </Box>
          </Stack>
          <Stack direction="row" spacing={3}>
            <Box sx={{ flex: 1 }}>
              <IMaskFormTextFieldNumber
                control={control}
                name="amount"
                label="Cost"
                disabled={isCreateMode ? userRole.isAuditor : isDisable}
                rules={isCreateMode ? { required: true } : undefined}
                {...(isCreateMode
                  ? getTextFieldColorStateProps("amount", formState.errors)
                  : {})}
              />
              {!isCreateMode && isUserSuperadmin === true && (
                <Esg12Label
                  onClick={() => {
                    setOpenValueFormatEditor(true);
                    setValueFormatName("costFormat");
                  }}
                  sx={{
                    textDecoration: "underline",
                    mt: "6px",
                    cursor: "pointer",
                  }}
                >
                  Change Value format?
                </Esg12Label>
              )}
            </Box>
            <Box sx={{ flex: 1 }}>
              <Controller
                name="currency"
                control={control}
                rules={
                  isCreateMode
                    ? { required: REQUIRED_VALIDATION_MESSAGE }
                    : undefined
                }
                render={({ field }) => (
                  <AutocompleteField
                    textFieldProps={{
                      label: (
                        <span>
                          Currency{" "}
                          {!isCreateMode &&
                            utilityBill &&
                            utilityBill.currencyCalculationFormula !== null && (
                              <Tooltip
                                title={
                                  <pre>
                                    {utilityBill.currencyCalculationFormula}
                                  </pre>
                                }
                                placement="top"
                                arrow
                              >
                                <IconButton
                                  aria-label="info"
                                  size="small"
                                  sx={{
                                    maxHeight: "23px",
                                    maxWidth: "23px",
                                    mt: -0.4,
                                  }}
                                >
                                  {utilityBill.currencyCalculationFormula !==
                                    null && <Infounit />}
                                </IconButton>
                              </Tooltip>
                            )}
                        </span>
                      ),
                      ...(isCreateMode
                        ? getTextFieldColorStateProps(
                            "currency",
                            formState.errors,
                          )
                        : getTextFieldNoErrorProps(
                            "currency",
                            isWarningFields,
                          )),
                    }}
                    autocompleteProps={{
                      ...field,
                      disabled: isCreateMode ? userRole.isAuditor : isDisable,
                      onChange: (e, data) => {
                        field.onChange(data);
                      },
                      options: allowedValues ? allowedValues.currencies : [],
                    }}
                  />
                )}
              />
            </Box>
          </Stack>
          {!isCreateMode && utilityBill?.spendBased && (
            <Stack direction="row" spacing={3} mt={2}>
              <div style={{ display: "flex", width: "50%" }}>
                <TextField
                  label={
                    <span>
                      Unit Cost Value{" "}
                      <Tooltip
                        title="This value is defined as an assumption in Site Management and is used to calculate usage for this bill"
                        placement="top"
                      >
                        <IconButton
                          aria-label="info"
                          size="small"
                          sx={{ maxHeight: "23px", maxWidth: "23px" }}
                        >
                          <InfoIcon />
                        </IconButton>
                      </Tooltip>
                    </span>
                  }
                  helperText={
                    <FormHelperText
                      sx={{
                        color: "red",
                        fontFamily: "Manrope",
                        fontSize: "11px",
                        whiteSpace: "pre-wrap",
                        overflow: "hidden",
                        width: "400px",

                        "&.MuiFormHelperText-root": {
                          margin: "0px",
                          marginLeft: "-11px",
                          color: "red",
                        },
                      }}
                    >
                      Unit Cost is defined at the Site/Utility Provider level
                      and needs to be modified under Site Management
                    </FormHelperText>
                  }
                  fullWidth
                  disabled
                  value={utilityBill.unitCost}
                />
              </div>
              <div style={{ flex: 1 }}>
                <TextField label="Unit Cost" fullWidth value={unitCostValue} />
              </div>
            </Stack>
          )}
        </Box>
        {!isCreateMode && (
          <Box mb={4}>
            <Label12SemiBold>Calculated emission</Label12SemiBold>
            <Stack direction="row" alignItems="center">
              {utilityBill?.type === "Refrigerants" ? (
                <Label14SemiBold>
                  {co2Emission !== null && co2Emission.toFixed(4) !== "0.0000"
                    ? co2Emission.toFixed(4)
                    : "-"}{" "}
                  t CO<sub>2</sub>
                </Label14SemiBold>
              ) : (
                <Label14SemiBold>
                  {co2Emission === null ? "-" : co2Emission.toFixed(4)} t CO
                  <sub>2</sub>
                </Label14SemiBold>
              )}
              {utilityBill?.explanation && (
                <Tooltip
                  title={
                    <pre>
                      {(() => {
                        const indexOfEmissions =
                          utilityBill?.explanation.indexOf(
                            "Emissions Factors Applied:",
                          );

                        if (indexOfEmissions !== -1) {
                          return [
                            <span
                              key="bold"
                              style={{
                                fontWeight: "1000",
                                fontFamily: "Manrope",
                                fontSize: "13px",
                              }}
                            >
                              {utilityBill?.explanation.substring(
                                0,
                                indexOfEmissions +
                                  "Emissions Factors Applied:".length,
                              )}
                            </span>,
                            utilityBill?.explanation.substring(
                              indexOfEmissions +
                                "Emissions Factors Applied:".length,
                            ),
                          ];
                        }
                        return utilityBill?.explanation?.replaceAll(
                          "null",
                          "-",
                        );
                      })()}
                    </pre>
                  }
                >
                  <Box sx={{ ml: 1 }}>
                    <QuestionIcon />
                  </Box>
                </Tooltip>
              )}
            </Stack>
          </Box>
        )}
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 2,
          }}
        >
          <SaveConfirmButtons
            isCreateMode={isCreateMode}
            isAuditor={userRole.isAuditor}
            utilityBill={utilityBill}
            isSaveButtonDisabled={isSaveButtonDisabled}
            isConfirmButtonDisabled={isConfirmButtonDisabled}
            isCreateButtonDisabled={isCreateButtonDisabled}
            onSaveChangesClick={openSaveChangesDialog}
            onConfirmDataClick={handleConfirmData}
            onCreateBillClick={handleSaveClick}
          />
          <StatusChangeButtons
            isCreateMode={isCreateMode}
            isAdditionalStatusesOn={isAdditionalStatusesOn}
            isNotApplicableInvalidDataButtonDisabled={
              isNotApplicableInvalidDataButtonDisabled
            }
            isNotApplicableCostOnlyButtonDisabled={
              isNotApplicableCostOnlyButtonDisabled
            }
            onSetStatus={handleSetStatus}
          />
        </Box>
      </Box>
      {!isCreateMode && (isUpdating || isLoading) ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
            minHeight: "100px",
            marginTop: 2,
          }}
        >
          <Loading sx={{ width: "50px" }} />
        </Box>
      ) : (
        !isCreateMode && (
          <Box
            sx={{
              marginTop: "20px",
              display: "flex",
              flexWrap: "wrap",
              paddingRight: "40px",
            }}
          >
            {utilityBill?.errorMessage ===
              DATES_VERIFICATION_ANOMALY.anomalyType && (
              <Anomaly
                anomalyType={DATES_VERIFICATION_ANOMALY.anomalyType}
                message={DATES_VERIFICATION_ANOMALY.messages}
              />
            )}
            {getAnomaliesData(utilityBill ?? ({} as UtilityBillByIdDTO))?.map(
              ({ anomalyType, message, previousBillGuid }, index) => (
                <Box
                  mb={3}
                  sx={{ minWidth: "fit-content", marginLeft: "20px" }}
                  key={`${index + 1}-anomaly`}
                >
                  <Anomaly
                    anomalyType={anomalyType}
                    message={message as string}
                    previousBillGuid={previousBillGuid}
                    notify={notify}
                  />
                </Box>
              ),
            )}
          </Box>
        )
      )}
      {!isCreateMode && (
        <UtilityBillSaveChangesDialog
          handleSaveClick={handleSaveClick}
          isOpen={isSaveChangesDialogOpen}
          onClose={closeSaveChangesDialog}
          varient={
            utilityBill?.errorMessage?.includes("Duplicate Bill Uploaded")
              ? "duplicateBill"
              : ""
          }
        />
      )}
      <AddOtherSitesDialog
        isOpen={openAddSiteDialog}
        onClose={() => setOpenAddSiteDialog(false)}
        handleSaveClick={handleApplyNewSite}
      />
      {!isCreateMode && (
        <>
          <DateFormatEditor
            isOpen={openDateFormatEditor}
            onClose={() => setOpenDateFormatEditor(false)}
            collectorId={newCollectorId ?? utilityBill?.collectorId ?? 0}
            data={formatData}
          />
          <ValueFormatEditor
            isOpen={openValueFormatEditor}
            onClose={() => setOpenValueFormatEditor(false)}
            collectorId={newCollectorId ?? utilityBill?.collectorId ?? 0}
            valueFormatName={valueFormatName}
            data={formatData}
          />
        </>
      )}
      {!isCreateMode && (
        <UtilityBillEditNotesDialog
          open={openNotesEdit}
          onClose={handleCloseEditNotes}
          notes={notes}
          notesError={notesError}
          onChange={handleEditNotes}
          onSave={handleSaveCollectorNotes}
        />
      )}
    </>
  );
};
